import { zodResolver } from '@hookform/resolvers/zod';
import { Slider } from '@miblanchard/react-native-slider';
import type { PhoneNumber } from 'libphonenumber-js';
import { parsePhoneNumber } from 'libphonenumber-js';
import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Platform, View } from 'react-native';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';
import { z } from 'zod';

import type { RentalLocation } from '@/api';
import type { Car } from '@/api/cars/types';
import { InputPhone } from '@/components/input-phone';
import type { PhoneCountry } from '@/components/input-phone/types';
import { MapView } from '@/components/map-view';
import {
  BASE_DELIVERY_EARNINGS,
  getEstimatedMaxDeliveryFee,
  translate,
  validatePhoneNumber,
} from '@/core';
import { GREY_10, GREY_30, GREY_60, PINKLING } from '@/style/colors';
import { ControlledInput, Text } from '@/ui';
import { CheckItem } from '@/ui/check-item';
import type { DropdownOption } from '@/ui/dropdown';
import Dropdown from '@/ui/dropdown';
import { Icon } from '@/ui/icon';
import { Car as CarIcon } from '@/ui/icons';

export const availabilityFormSchema = z.object({
  minDaysToBook: z.string().min(1, 'Minimum rental days is required'),
  hoursBetweenTrips: z.string().min(1, 'Hours between trips is required'),
  minHoursInAdvance: z.string().min(1, 'Minimum hours in advance is required'),
  supportsDelivery: z.boolean(),
  maxDeliveryDistance: z.string().optional(),
  deliveryPersonPhone: z.string().optional(),
});

export type AvailabilityFormType = z.infer<typeof availabilityFormSchema>;

export default function AvailabilityForm({
  car,
  onValidChange,
  onChange,
  pickupLocation,
  deliveryDistanceCostOptions,
  defaultValues,
}: {
  car: Car;
  onValidChange: (isValid: boolean) => void;
  onChange: (values: AvailabilityFormType) => void;
  pickupLocation: RentalLocation;
  deliveryDistanceCostOptions?: Record<string, number>;
  defaultValues?: Partial<AvailabilityFormType>;
}) {
  const { styles, theme } = useStyles(stylesheet);
  const [addDeliveryAgent, setAddDeliveryAgent] = useState(
    (defaultValues?.deliveryPersonPhone ?? car.deliveryPersonPhone) !== ''
  );
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState<PhoneCountry>();
  const [mapZoom, setMapZoom] = useState(13);
  const [mapAltitude, setMapAltitude] = useState(3000);

  const {
    control,
    trigger,
    setValue,
    getValues,
    formState: { isValid, errors },
    watch,
  } = useForm<AvailabilityFormType>({
    defaultValues: {
      minDaysToBook:
        defaultValues?.minDaysToBook ?? (car.minDaysToBook?.toString() || '1'),
      hoursBetweenTrips:
        defaultValues?.hoursBetweenTrips ??
        (car.hoursBetweenTrips?.toString() || '3'),
      minHoursInAdvance:
        defaultValues?.minHoursInAdvance ??
        (car.minHoursInAdvance?.toString() || '3'),
      supportsDelivery: defaultValues?.supportsDelivery ?? car.supportsDelivery,
      maxDeliveryDistance:
        defaultValues?.maxDeliveryDistance ??
        (car.maxDeliveryDistance?.toString() || '10'),
      deliveryPersonPhone:
        defaultValues?.deliveryPersonPhone ?? car.deliveryPersonPhone,
    },
    resolver: zodResolver(availabilityFormSchema),
  });

  useEffect(() => {
    onValidChange(isValid);
  }, [isValid]);

  useEffect(() => {
    const sub = watch(() => {
      onChange(getValues());
    });
    return () => sub.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (defaultValues) {
      trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [
    maxDeliveryDistance,
    hoursBetweenTrips,
    minHoursInAdvance,
    supportsDelivery,
    deliveryPersonPhone,
  ] = useWatch({
    control,
    name: [
      'maxDeliveryDistance',
      'hoursBetweenTrips',
      'minHoursInAdvance',
      'supportsDelivery',
      'deliveryPersonPhone',
    ],
  });

  useEffect(() => {
    if (!maxDeliveryDistance) return;

    const distance = parseInt(maxDeliveryDistance.toString(), 10);

    let zoom = 13;
    if (distance <= 10) zoom = 10;
    else if (distance <= 20) zoom = 9.5;
    else if (distance <= 35) zoom = 9;
    else if (distance <= 50) zoom = 8.5;
    else if (distance <= 65) zoom = 8;
    else if (distance <= 85) zoom = 7.6;
    else if (distance <= 110) zoom = 7.3;
    else if (distance <= 130) zoom = 7;
    else zoom = 6.5;

    setMapZoom(zoom);

    let altitude = 3000;
    if (distance <= 10) altitude = 100000;
    else if (distance <= 20) altitude = 100000;
    else if (distance <= 35) altitude = 200000;
    else if (distance <= 50) altitude = 300000;
    else if (distance <= 65) altitude = 400000;
    else if (distance <= 85) altitude = 500000;
    else if (distance <= 110) altitude = 600000;
    else if (distance <= 130) altitude = 700000;
    else altitude = 1000000;
    setMapAltitude(altitude);
  }, [maxDeliveryDistance]);

  const parsedPhoneNumber = React.useMemo((): PhoneNumber | null => {
    if (!deliveryPersonPhone) return null;

    const phoneNumber = parsePhoneNumber(deliveryPersonPhone);

    return phoneNumber;
  }, [deliveryPersonPhone]);

  const deliveryCost = useMemo(
    () =>
      getEstimatedMaxDeliveryFee(
        maxDeliveryDistance,
        deliveryDistanceCostOptions
      ),
    [deliveryDistanceCostOptions, maxDeliveryDistance]
  );

  useEffect(() => {
    setPhone(parsedPhoneNumber?.nationalNumber ?? '');
  }, [parsedPhoneNumber]);

  const isValidPhoneNumber = useMemo(() => {
    if (!country || !phone) return false;

    return validatePhoneNumber(country, phone);
  }, [country, phone]);

  useEffect(() => {
    if (isValidPhoneNumber && phone && country) {
      setValue('deliveryPersonPhone', `+${country?.callingCode}${phone}`);
    } else {
      setValue('deliveryPersonPhone', '');
    }
  }, [isValidPhoneNumber, phone, country]);

  const content = (
    <View style={{ flexGrow: 1, gap: 24 }}>
      <View
        style={{
          gap: 16,
        }}
      >
        <Text type="title2">Delivery</Text>

        <CheckItem
          text="Available For Delivery"
          caption={translate('partner.cars.deliveryEarningsCaption', {
            amount: BASE_DELIVERY_EARNINGS.toFixed(2),
          })}
          onPress={() => {
            setValue('supportsDelivery', !supportsDelivery);
            trigger();
          }}
          active={supportsDelivery}
          type="checkbox"
        />

        {supportsDelivery && pickupLocation ? (
          <View style={{ gap: 8 }}>
            <Text type="bodyBold">Maximum delivery distance (miles)</Text>

            <View
              style={{
                height: 300,
                width: '100%',
                borderRadius: 16,
                overflow: 'hidden',
              }}
            >
              <MapView
                markers={[
                  {
                    latitude: pickupLocation.latitude,
                    longitude: pickupLocation.longitude,
                    icon: (
                      <Icon
                        type="naked"
                        icon={<CarIcon />}
                        fillColor={theme.colors.icon.color}
                        width={14}
                        height={14}
                      />
                    ),
                  },
                ]}
                defaultCenter={{
                  latitude: pickupLocation.latitude,
                  longitude: pickupLocation.longitude,
                }}
                fitBounds={false}
                altitude={mapAltitude}
                zoom={mapZoom}
                showsUserLocation={false}
                style={{ borderRadius: 16 }}
                circles={[
                  {
                    center: {
                      latitude: pickupLocation.latitude,
                      longitude: pickupLocation.longitude,
                    },
                    radius: maxDeliveryDistance * 1609.34, // convert miles to meters
                    strokeColor: theme.colors.icon.brandColor,
                    strokeWidth: 2,
                    fillColor: theme.colors.chip.brand.backgroundColor,
                  },
                ]}
              />
            </View>

            <View>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Text type="title2">{maxDeliveryDistance} miles</Text>

                <Text type="caption1">
                  Estimated max delivery fee: ${deliveryCost?.toFixed(2)}
                </Text>
              </View>

              <Slider
                value={Number(maxDeliveryDistance)}
                onSlidingComplete={(values) => {
                  setValue('maxDeliveryDistance', String(values[0]));
                }}
                onValueChange={(values) => {
                  setValue('maxDeliveryDistance', String(values[0]));
                }}
                minimumValue={10}
                maximumValue={150}
                step={5}
                minimumTrackTintColor={PINKLING}
                maximumTrackTintColor={
                  UnistylesRuntime.themeName === 'dark' ? GREY_60 : GREY_30
                }
                thumbTintColor={
                  UnistylesRuntime.themeName === 'dark' ? GREY_10 : GREY_60
                }
              />
            </View>

            <View>
              <CheckItem
                text="Add Delivery Agent"
                caption="Agents assigned to this car will also receive delivery instructions. They’ll get the car information, pickup and delivery addresses, guest details, and several reminders to deliver the car."
                onPress={() => {
                  if (addDeliveryAgent === true) {
                    setValue('deliveryPersonPhone', '');
                  }

                  setAddDeliveryAgent(!addDeliveryAgent);
                }}
                active={addDeliveryAgent}
                type="checkbox"
              />
            </View>

            {addDeliveryAgent ? (
              <View>
                <InputPhone
                  onChangeText={(text: string) => setPhone(text)}
                  onCountryChange={(c: PhoneCountry) => setCountry(c)}
                  defaultCountryCode={parsedPhoneNumber?.country}
                  phone={parsedPhoneNumber?.nationalNumber}
                />
              </View>
            ) : null}
          </View>
        ) : null}
      </View>

      <View style={{ gap: 24, flexGrow: 1, paddingBottom: 32 }}>
        <Text type="title2">Availability</Text>

        <ControlledInput
          placeholder="Enter a number"
          control={control}
          name="minDaysToBook"
          label="Minimum Rental Days"
          keyboardType="number-pad"
          onBlur={() => {
            trigger('minDaysToBook');
          }}
          onChangeText={(text) => {
            setValue('minDaysToBook', text);
            trigger('minDaysToBook');
          }}
          tooltipText="Your car will only appear in search results if the guest selects a trip that is this number of days or longer. We recommend setting this to 1"
        />

        <Dropdown
          mode="single"
          label="Minimum Time Between Rentals"
          options={[
            { id: '3', value: '3', title: '3 Hours' },
            { id: '6', value: '6', title: '6 Hours' },
            { id: '12', value: '12', title: '12 Hours' },
            { id: '24', value: '24', title: '24 Hours' },
          ]}
          // placeHolderText="Select your car's pickup location"
          onSelect={(selected: DropdownOption[]) => {
            setValue('hoursBetweenTrips', selected[0].id);
            trigger();
          }}
          onClear={() => {
            setValue('hoursBetweenTrips', '');
            trigger('hoursBetweenTrips');
          }}
          tooltipText="Sets a buffer between the end of one trip and the start of the next. We hold the calendar for this time so you can clean, charge, or handle handoff. Existing reservations stay as is.
          Example: 3 hours means a trip ending at 5 pm makes the next eligible start 8 pm."
          values={hoursBetweenTrips ? [hoursBetweenTrips.toString()] : []}
          error={errors.hoursBetweenTrips?.message}
        />

        <Dropdown
          mode="single"
          label="Minimum Time In Advance"
          options={[
            { id: '3', value: '3', title: '3 Hours' },
            { id: '6', value: '6', title: '6 Hours' },
            { id: '12', value: '12', title: '12 Hours' },
            { id: '24', value: '24', title: '24 Hours' },
          ]}
          onSelect={(selected: DropdownOption[]) => {
            setValue('minHoursInAdvance', selected[0].id);
            trigger();
          }}
          onClear={() => {
            setValue('minHoursInAdvance', '');
            trigger('minHoursInAdvance');
          }}
          tooltipText="Requires lead time for new bookings. Guests can only start a trip after this window from the moment they book. Higher values reduce last-minute bookings.
          Example: with 3 hours, a booking made at 2 pm cannot start before 5 pm today."
          values={minHoursInAdvance ? [minHoursInAdvance?.toString()] : []}
          error={errors.minHoursInAdvance?.message}
        />
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {Platform.OS === 'web' ? content : content}
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
    gap: 16,
    maxWidth: {
      xs: '100%',
      md: 600,
    },
  },
}));
